import { getProfitabilityAnalysis } from "@/api/financialReport";
import { resolveDateRange } from "@/store/financialReports/utils/dateRange";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// Módulo del drawer "Análisis de Categorías" del tab Rentabilidad: la
// rentabilidad del rango del tab abierta por ítem de una categoría
// (tiendas/marcas). Espejo de `salesAnalysis`: el body del drawer lee de acá
// y las cards son presentacionales. El ítem del gráfico por ítem no es estado
// propio: es la selección del filtro cuando queda un solo ítem tildado (ver
// el body).
//
// El período NO es estado propio (plan de filtros, 2026-10-05): el drawer
// comparte la granularidad y el rango del tab (`financialReports.filterPeriod`
// y `reportsFilters`), así que cambiarlos adentro también los cambia afuera.
// El getter `window` los resuelve con el mismo `resolveDateRange` que usan las
// cabeceras de la tabla.

const state = () => ({
	/** Categoría principal (la del chip o la del filtro del drawer). */
	category: null,
	/**
	 * Selección de los filtros de categoría: { tiendas: ["T1","T2"] }.
	 * Arranca vacía (= todos los ítems); lo que viaja al backend es sólo la
	 * selección PARCIAL — ver `effectiveFilters`.
	 */
	selectedItems: {},
	/** Payload del backend, congelado (sin observer profundo de Vue 2). */
	data: null,
	isLoading: false,
	error: null,
	/** Número del último pedido: descarta respuestas que llegan tarde. */
	requestId: 0,
});

const getters = {
	/** Rango y granularidad del tab, los mismos de la tabla de Rentabilidad. */
	window: (_state, _getters, rootState) => {
		const filterPeriod = rootState.financialReports.filterPeriod;
		const filters = rootState.reportsFilters;
		const { initDate, endDate } = resolveDateRange(
			filters.endMonth,
			filters.selectedPeriodRange,
			filterPeriod,
			filters.customRange,
			filters.customYearRange,
			filters.dataFirstMonth,
		);
		return { initDate, endDate, period: filterPeriod };
	},
};

const mutations = {
	SET_CONTEXT(state, { category, selectedItems }) {
		state.category = category ?? null;
		state.selectedItems = Object.freeze(selectedItems ?? {});
	},
	SET_DATA(state, payload) { state.data = payload; },
	SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
	SET_ERROR(state, error) { state.error = error; },
	NEXT_REQUEST(state) { state.requestId += 1; },
	RESET(state) {
		state.category = null;
		state.selectedItems = {};
		state.data = null;
		state.error = null;
	},
};

/**
 * Filtro que viaja al backend. Una categoría sin nada tildado (el estado con
 * el que abre el drawer) o con TODOS sus ítems tildados no filtra: hacerlo
 * dejaría afuera los documentos sin clasificar en ella ("Sin tienda") y el
 * total del drawer ya no cuadraría con la tabla del tab. Sólo viaja la
 * selección parcial.
 */
export function effectiveFilters(selectedItems, itemsByCategory = {}) {
	const filters = {};
	Object.entries(selectedItems || {}).forEach(([category, values]) => {
		const all = itemsByCategory[category] || [];
		if (values && values.length && values.length < all.length) {
			filters[category] = values;
		}
	});
	return Object.keys(filters).length ? filters : null;
}

const actions = {
	/** Pide el análisis del rango del tab para la categoría y su selección. */
	async fetchProfitabilityAnalysis(
		{ commit, getters, rootState, state },
		{ category = null, selectedItems = null } = {},
	) {
		commit("SET_CONTEXT", { category, selectedItems });
		commit("NEXT_REQUEST");
		const requestId = state.requestId;
		commit("SET_LOADING", true);
		commit("SET_ERROR", null);
		try {
			const filters = effectiveFilters(
				selectedItems, rootState.classification.itemsByCategory
			);
			const response = await getProfitabilityAnalysis({
				...getters.window, category, selectedItems: filters,
			});
			if (requestId === state.requestId) commit("SET_DATA", Object.freeze(response.data));
		} catch (error) {
			if (requestId !== state.requestId) return;
			consoleHandlerError({ error, prefix: "[profitabilityAnalysis]", silent: true });
			commit(
				"SET_ERROR",
				extractErrorMessage(error, "Error al cargar el análisis de categorías.")
			);
		} finally {
			if (requestId === state.requestId) commit("SET_LOADING", false);
		}
	},

	/** Vuelve a pedir con la categoría y la selección vigentes (cambió el
	 *  rango o la granularidad). */
	refetchProfitabilityAnalysis({ dispatch, state }) {
		if (!state.category) return null;
		return dispatch("fetchProfitabilityAnalysis", {
			category: state.category, selectedItems: state.selectedItems,
		});
	},

	/**
	 * Un filtro de categoría acota ítems y, si es de otra categoría, la vuelve
	 * la principal sin nada tildado (D7: pasar de Tiendas a Marcas sin cerrar
	 * el drawer). Ya siendo la principal, se respeta lo que el analista tilde
	 * o destilde ("Ver todos" vuelve a vaciar).
	 */
	changeCategory({ dispatch, state }, { id, values }) {
		const isNewCategory = id !== state.category;
		return dispatch("fetchProfitabilityAnalysis", {
			category: id,
			selectedItems: { [id]: isNewCategory ? [] : [...(values || [])] },
		});
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
